import * as z from "zod/v4";

const timePeriodSchema = z.enum([
  "day",
  "week",
  "month",
  "last-month",
  "quarter",
  "all",
  "custom",
]);

const customRangeSchema = z.object({
  start: z.string().min(1),
  end: z.string().min(1),
});

const stepProgressSchema = z.object({
  completed: z.boolean(),
  durationSeconds: z.number().nullable(),
});

// Schema for cumulative step data (timeSpent and visited tracking)
const cumulativeSubstepDataSchema = z.object({
  timeSpent: z.number().optional(),
  visited: z.boolean().optional(),
});

const cumulativeStepDataSchema = z.object({
  timeSpent: z.number().optional(),
  visited: z.boolean().optional(),
  substeps: z.record(z.string(), cumulativeSubstepDataSchema).optional(),
});

const cumulativeStepTimesSchema = z
  .record(z.string(), cumulativeStepDataSchema)
  .nullable()
  .optional();

const processingRuleSchema = z.object({
  processAt: z.enum(["open", "in_transit", "delivered", "manual"]),
  delay: z.number(),
});

const coverageRuleSchema = z.object({
  basePrice: z.number().nullable(),
  adjustment: z.number().nullable(),
  productName: z.string(),
  productSubheading: z.string(),
  excludedProductTags: z.array(z.string()),
  excludedCollections: z.array(z.string()),
  excludedCustomerTags: z.array(z.string()),
  excludedProperties: z.array(z.string()),
});

const billingAddressSchema = z.object({
  line1: z.string().nullable(),
  line2: z.string().nullable(),
  city: z.string().nullable(),
  state: z.string().nullable(),
  postal_code: z.string().nullable(),
  country: z.string().nullable(),
});

const paymentMethodSchema = z
  .object({
    id: z.string().nullable(),
    customer: z.string().nullable(),
    type: z.enum(["card", "bank account"]),
    brand: z.string().nullable(),
    last4: z.string().nullable(),
    expiration: z.string().nullable(),
    accountType: z.string().nullable(),
    accountHolderType: z.string().nullable(),
    billingDetails: z
      .object({
        name: z.string().nullable(),
        email: z.string().nullable(),
        address: billingAddressSchema.nullable(),
      })
      .nullable(),
  })
  .nullable();

export const coverageConfigSchema = z.object({
  titleText: z.string().nullable().optional(),
  subtitleText: z.string().nullable().optional(),
  titleFontSize: z.number().nullable().optional(),
  titleColor: z.string().nullable().optional(),
  subtitleFontSize: z.number().nullable().optional(),
  subtitleColor: z.string().nullable().optional(),
  iconType: z.string().nullable().optional(),
  customLogoUrl: z.string().nullable().optional(),
  backgroundColor: z.string().nullable().optional(),
  cardBorderRadius: z.number().nullable().optional(),
  buttonColor: z.string().nullable().optional(),
  buttonTextColor: z.string().nullable().optional(),
  buttonFontSize: z.number().nullable().optional(),
  buttonBorderRadius: z.number().nullable().optional(),
  optOutText: z.string().nullable().optional(),
  optOutTextColor: z.string().nullable().optional(),
  optOutFontSize: z.number().nullable().optional(),
});

const infoModalBenefitSchema = z.object({
  text: z.string(),
  subtext: z.string(),
});

const infoModalConfigSchema = z.object({
  leftSideImage: z.string().nullable().optional(),
  logo: z.string().nullable().optional(),
  headingText: z.string().nullable().optional(),
  bodyText: z.string().nullable().optional(),
  benefits: z.array(infoModalBenefitSchema).nullable().optional(),
});

const placementConfigSchema = z
  .object({
    selector: z.string(),
    insertionPosition: z.enum(["after", "before", "prepend", "append"]),
    shadowRootSelector: z.string().nullable().optional(),
    capturedAt: z.string().optional(),
    validatedBy: z.enum(["merchant", "admin"]).optional(),
    testToken: z.string().nullable().optional(),
    screenshotUrl: z.string().nullable().optional(),
    coverageConfig: coverageConfigSchema.nullable().optional(),
    checkoutButtonText: z.string().nullable().optional(),
    infoModalConfig: infoModalConfigSchema.nullable().optional(),
  })
  .nullable();

const returnAddressSchema = z.object({
  address1: z.string(),
  address2: z.string().nullable(),
  city: z.string(),
  state: z.string(),
  zip: z.string(),
  country: z.string(),
  company: z.string().nullable().optional(),
});

const portalUISettingsSchema = z
  .object({
    logoUrl: z.string().nullable(),
    loginBackgroundUrl: z.string().nullable(),
    loginHeading: z.string().nullable(),
    loginSubheading: z.string().nullable(),
    loginHeadingFontSize: z.string().nullable(),
    loginSubheadingFontSize: z.string().nullable(),
    backToStoreButtonColor: z.string().nullable(),
    returnPolicyUrl: z.string().nullable(),
  })
  .nullable();

// Communications settings collected during onboarding (returns only)
const communicationsInfoSchema = z
  .object({
    senderEmail: z.string().nullable().optional(),
    notifyOnReturnCreated: z.boolean().nullable().optional(),
    notifyOnReturnDelivered: z.boolean().nullable().optional(),
    notifyOnReturnNeedsReview: z.boolean().nullable().optional(),
    returnNotificationEmail: z.string().nullable().optional(),
    billingNotificationEmail: z.string().nullable().optional(),
  })
  .nullable()
  .optional();

const onboardingExperienceSubstepSchema = z.object({
  visited: z.boolean(),
  timeSpent: z.number().nullable(),
});

const onboardingExperienceStepSchema = z.object({
  visited: z.boolean(),
  timeSpent: z.number().nullable(),
  substeps: z.record(z.string(), onboardingExperienceSubstepSchema).optional(),
});

const onboardingExperienceSchema = z
  .object({
    startedAt: z.string().nullable(),
    endedAt: z.string().nullable(),
    onboardingProgress: z
      .enum([
        "completed",
        "in-progress",
        "partial-completion",
        "awaiting-approval",
        "help-requested",
      ])
      .optional(),
    steps: z.record(z.string(), onboardingExperienceStepSchema),
  })
  .nullable();

// Merchant notes/questions schema
const merchantNoteSchema = z.object({
  id: z.string(),
  text: z.string(),
  stepId: z.string(),
  substepId: z.string(),
  timestamp: z.string(),
});

const configurationPayloadSchema = z
  .object({
    returnAddress: returnAddressSchema.nullable(),
    returnShipping: z.enum(["customer", "merchant"]).nullable(),
    exchangeShipping: z.enum(["customer", "merchant"]).nullable(),
    coverageEnabled: z.boolean().nullable(),
    coveragePrice: z.number().nullable(),
    priceAdjustment: z.number().nullable(),
    totalCoveragePrice: z.number().nullable(),
    coverageConfig: z.any().nullable(),
    paymentMethod: paymentMethodSchema,
    processingAutomation: z
      .object({
        refunds: processingRuleSchema,
        exchanges: processingRuleSchema,
        storeCredit: processingRuleSchema,
      })
      .nullable(),
    returnFlowJson: z.any().nullable(),
    policyData: z.any().nullable(),
    portalUISettings: z.any().nullable(),
    placementConfig: placementConfigSchema,
  })
  .nullable();

// Lightweight schema for table rows (minimal fields, no images/configs)
export const selfServeOnboardingRowSchema = z.object({
  id: z.string(),
  merchantId: z.string(),
  name: z.string(),
  industry: z.string(),
  type: z.enum(["Self-Serve", "Custom"]),
  startDateTime: z.string(),
  completionDateTime: z.string().nullable(),
  lastSessionAt: z.string(),
  dealSize: z.number().nullable(),
  onboardingProgress: z.enum([
    "Completed",
    "Reopened",
    "Awaiting approval",
    "In progress",
    "Partial Completion",
    "Help Requested",
  ]),
  pushConfigurationDate: z.string().nullable(),
  // Duration from tracked time stats (in seconds)
  durationSeconds: z.number().nullable(),
  // Total number of sessions for this merchant
  totalSessions: z.number().nullable(),
  // Completion percentage (completed substeps / total substeps)
  completionPercent: z.number().nullable(),
  // Weighted revenue (progress-weighted deal size)
  weightedRevenue: z.number().nullable(),
  // Weight percentage (0-100) based on visited substeps
  weightPercent: z.number().nullable(),
  // Step times data for weight breakdown display
  cumulativeStepTimes: z.any().optional(),
  // Order-editing specific fields (only populated for order-editing product)
  latestOnboardingData: z.any().optional(),
  // Product type (returns or order-editing)
  product: z.enum(["returns", "order-editing"]).optional(),
  // HubSpot deal ID for linking to HubSpot
  hubspotDealId: z.string().nullable().optional(),
  contacted: z.boolean().optional(),
  contactedAt: z.string().nullable().optional(),
  contactedBy: z.string().nullable().optional(),
});

// Full schema with all details (used when sidebar opens)
export const selfServeOnboardingSchema = z.object({
  id: z.string(),
  merchantId: z.string(),
  name: z.string(),
  industry: z.string(),
  startDateTime: z.string(),
  completionDateTime: z.string().nullable(),
  // Last session/activity timestamp for "last updated" display
  lastSessionAt: z.string().nullable().optional(),
  // Total time spent in onboarding (in seconds)
  totalTimeSeconds: z.number().nullable().optional(),
  dealSize: z.number().nullable(),
  // Total number of sessions for this merchant
  totalSessions: z.number().nullable(),
  onboardingProgress: z.enum([
    "Completed",
    "Reopened",
    "Awaiting approval",
    "In progress",
    "Partial Completion",
    "Help Requested",
  ]),
  pushConfigurationDate: z.string().nullable(),
  onboardingSteps: z.object({
    productSelection: stepProgressSchema,
    policies: stepProgressSchema,
    returnOptions: stepProgressSchema,
    returnFlow: stepProgressSchema,
    coverage: stepProgressSchema,
    review: stepProgressSchema,
    goLive: stepProgressSchema,
  }),
  returnAddress: returnAddressSchema.nullable(),
  returnShipping: z.enum(["customer", "merchant"]).nullable(),
  exchangeShipping: z.enum(["customer", "merchant"]).nullable(),
  coverageRules: z.array(coverageRuleSchema).nullable(),
  coverageEnabled: z.boolean().nullable(),
  portalUISettings: portalUISettingsSchema,
  paymentMethod: paymentMethodSchema,
  processingAutomation: z
    .object({
      refunds: processingRuleSchema,
      exchanges: processingRuleSchema,
      storeCredit: processingRuleSchema,
    })
    .nullable(),
  returnFlowJson: z.any().nullable(),
  configurationPayload: configurationPayloadSchema,
  experience: onboardingExperienceSchema,
  placementConfig: placementConfigSchema,
  stepsRaw: z.record(z.string(), z.any()).nullable().optional(),
  onboardingDataRaw: z.any().nullable().optional(),
  startedAtRaw: z.string().nullable().optional(),
  endedAtRaw: z.string().nullable().optional(),
  // Cumulative step times with visited tracking from master document
  cumulativeStepTimes: cumulativeStepTimesSchema,
  // Custom configuration steps (from self-serve-onboarding-configurations)
  stepsConfig: z
    .object({
      steps: z.record(z.string(), z.array(z.string())),
      skipBilling: z.boolean().nullable().optional(),
      skipCoverage: z.boolean().nullable().optional(),
      customCoveragePrice: z.number().nullable().optional(),
    })
    .nullable()
    .optional(),
  // Expected substeps count from the latest session (stored in masters document)
  expectedSubsteps: z.number().nullable().optional(),
  // Whether billing step was skipped (stored in masters document)
  skipBilling: z.boolean().nullable().optional(),
  // Whether coverage step was skipped (stored in masters document)
  skipCoverage: z.boolean().nullable().optional(),
  // Completion percentage (calculated from cumulativeStepTimes / expectedSubsteps)
  completionPercent: z.number().nullable().optional(),
  // Merchant notes/questions from the onboarding process
  notes: z.array(merchantNoteSchema).nullable().optional(),
  // Communications settings (returns only)
  communicationsInfo: communicationsInfoSchema,
  contacted: z.boolean().optional(),
  contactedAt: z.string().nullable().optional(),
  contactedBy: z.string().nullable().optional(),
});

export const getSelfServeOnboardings = {
  name: "getSelfServeOnboardings" as const,
  input: z
    .object({
      limit: z.number().min(1).max(100).default(30),
      offset: z.number().min(0).default(0),
      timePeriod: timePeriodSchema,
      customRange: customRangeSchema.optional(),
      product: z.enum(["returns", "order-editing"]).optional(),
      statuses: z
        .array(
          z.enum([
            "Completed",
            "Reopened",
            "Awaiting approval",
            "In progress",
            "Partial Completion",
            "Help Requested",
          ]),
        )
        .optional(),
      searchQuery: z.string().optional(),
      // Column filters
      configurationPushedFilter: z.enum(["yes", "no"]).optional(),
      startDateRange: z
        .object({ start: z.string().min(1), end: z.string().min(1) })
        .optional(),
      durationFilter: z
        .object({ operator: z.enum(["lt", "gt"]), value: z.number().min(0) })
        .optional(),
      dealSizeFilter: z
        .object({
          operator: z.enum(["lt", "gt", "between"]),
          value: z.number().min(0),
          maxValue: z.number().min(0).optional(),
        })
        .optional(),
      // Filter out low intent onboardings (< 1 min duration)
      filterLowIntent: z.boolean().optional(),
      // Filter by onboarding type (Self-Serve or Custom)
      typeFilter: z.enum(["all", "Self-Serve", "Custom"]).optional(),
      // Filter by assignee email (show only onboardings assigned to this user)
      assigneeEmail: z.string().optional(),
      // Search assignees by partial name or email — array of terms, OR logic
      assigneeSearch: z.array(z.string()).optional(),
      // Include merchants with no assignees in results
      includeUnassigned: z.boolean().optional(),
    })
    .superRefine((value, ctx) => {
      if (value.timePeriod === "custom" && !value.customRange) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "customRange is required when timePeriod is custom",
          path: ["customRange"],
        });
      }
    }),
  output: z.object({
    onboardings: z.array(selfServeOnboardingRowSchema), // Use lightweight schema for table rows
    total: z.number(),
    limit: z.number(),
    offset: z.number(),
  }),
};

export type SelfServeOnboardingRow = z.infer<
  typeof selfServeOnboardingRowSchema
>;
export type SelfServeOnboarding = z.infer<typeof selfServeOnboardingSchema>;
export type StepProgress = z.infer<typeof stepProgressSchema>;
export type ProcessingRule = z.infer<typeof processingRuleSchema>;
export type CoverageRule = z.infer<typeof coverageRuleSchema>;
export type PaymentMethod = z.infer<typeof paymentMethodSchema>;
export type MerchantNote = z.infer<typeof merchantNoteSchema>;
export type GetSelfServeOnboardingsInput = z.infer<
  typeof getSelfServeOnboardings.input
>;
export type GetSelfServeOnboardingsOutput = z.infer<
  typeof getSelfServeOnboardings.output
>;
